/* istanbul ignore file */
import serialize from 'serialize-javascript';

import noop from 'lodash/noop';
import { Style } from 'nordic/style';
import { Script } from 'nordic/script';
import { GTM } from 'nordic/gtm';
import { Hotjar } from 'nordic/hotjar';
import { MelidataTrack } from 'nordic/melidata/melidata-track';
import {
  TrackingProvider,
  CalmProvider,
} from '@pog-ui-landings-library/framework';

import {
  SECTIONS_BY_VIEW,
  THEMED_COMPONENT_PROVIDERS,
  UNIQUE_INSTANCE_SECTIONS,
} from '../../../utils/constants';
import { THEMES } from '../../../utils/themes';
import {
  hasRemoteModules,
  getSectionName,
  getUniqueSectionTypes,
} from './section-helpers';
import { getTrackHandlers } from './tracking-helpers';
import LandingHead from '../components/LandingHead';
import SectionStyles from '../components/SectionStyles';
import CalmComponent from '../components/CalmComponent';
import ModalRebrand from '../components/ModalRebrand';

const filterViewSections = (sections, enabledSections) => {
  const limitedSections = new Set();

  return sections.filter(({ type }) => {
    const normalizedType = type.toLowerCase();

    if (!enabledSections.includes(normalizedType)) {
      return false;
    }

    if (UNIQUE_INSTANCE_SECTIONS.includes(normalizedType)) {
      if (limitedSections.has(normalizedType)) {
        return false;
      }

      limitedSections.add(normalizedType);
    }

    return true;
  });
};

export const getContentView = (props, view, ExternalComponent = null) => {
  const {
    sections = [],
    experiments = {},
    mainStyles = {},
    imagesPrefix = '/',
    scope = '',
    isWebView = false,
    initCallback = noop,
    isMobile,
    landingConfig: {
      groupId,
      variantId,
      landingId,
      landingVersionId: versionId,
      variantConfigurationId,
      variantName,
      head,
      productName,
      space,
      tracking: { gtm, melidata, hotjar },
      custom: { ddrumConfig = null },
    },
    locale,
    device,
    landingStatus,
    isPreview,
    recaptchaSiteKey,
    coreValues: {
      userId,
      coupon = null,
      channel = null,
      subChannel = null,
      siteId = null,
      userAgent = null,
    },
    baseApiPath,
    csrfToken,
    isRebrand,
    theme,
    translationsRebrand,
    hiddenModal,
    keepImageURLs = false,
    clientLogsEnabled = false,
    utmParams,
    uuid,
  } = props;
  const { skipCalmTrack, calmTrack, enableShowTrack } = melidata;

  const enabledSections = ExternalComponent
    ? [...SECTIONS_BY_VIEW.calm, ...SECTIONS_BY_VIEW[view]]
    : SECTIONS_BY_VIEW.calm;

  const filteredSections = filterViewSections(sections, enabledSections);

  const uniqueSectionsTypes = getUniqueSectionTypes(filteredSections);
  const hasRemoteModuleSections = hasRemoteModules(filteredSections);

  // Set handlers the components will call to report tracks
  const trackHandle = getTrackHandlers(gtm);

  return (
    <div
      id="s-landing"
      className={`s-landing s-landing--${view}`}
      style={mainStyles}
      data-variant-name={variantName}
      data-product-name={productName}
      ref={initCallback}
    >
      <Style
        href={isMobile ? 'landingMobile.css' : 'landingDesktop.css'}
        critical
      />

      {theme === THEMES.MP && !isRebrand && (
        <Style href="rebrand-legacy.css" critical />
      )}

      {isPreview && isRebrand && <Style href="rebrand-fallback.css" critical />}

      <SectionStyles
        types={uniqueSectionsTypes}
        theme={theme}
        isMobile={isMobile}
        isThemed={THEMED_COMPONENT_PROVIDERS.includes(view)}
      />

      <LandingHead head={head} scope={scope} />

      {isRebrand && (
        <ModalRebrand
          isMobile={isMobile}
          translations={translationsRebrand}
          hidden={hiddenModal}
        />
      )}

      <div className="s-sections">
        <CalmProvider context={{ isRebrand, keepImageURLs }}>
          <TrackingProvider trackHandler={trackHandle}>
            {filteredSections?.map((section) => {
              const { id, config, type, section_id: sectionId } = section;
              const { anchor, alias, id: configId, form } = config || {};
              const key = `${type}__${id}`;

              config.tracking = {
                ...(config?.tracking || {}),
                onShow: {
                  ...(config?.tracking?.onShow || {}),
                  enabled: enableShowTrack,
                },
              };

              const sectionMetadata = {
                alias: alias || type,
                sectionId,
                sectionVersionId: id,
                type,
              };

              const formRequestMetadata = {
                landing_id: landingId,
                landing_group_id: groupId,
                variant_name: variantName,
                uuid,
                ...utmParams,
              };

              // FIXME: Homogenize alias in data to follow sections' schemas (They declare id as attribute).
              const sectionID = configId || anchor || type;

              const sectionName = getSectionName(type.toLowerCase());

              if (form?.recaptcha) {
                const recaptchaConfig = form.recaptcha;
                const recaptcha =
                  typeof recaptchaConfig === 'object' ? recaptchaConfig : {};

                config.form = {
                  ...(form || {}),
                  recaptcha: {
                    ...recaptcha,
                    key: recaptcha.key || recaptchaSiteKey,
                  },
                };
              }

              const Component =
                ExternalComponent &&
                SECTIONS_BY_VIEW[view].includes(sectionName)
                  ? ExternalComponent
                  : CalmComponent;

              return (
                <Component
                  type={sectionName}
                  id={sectionID}
                  key={key}
                  config={config}
                  isMobile={isMobile}
                  isWebView={isWebView}
                  theme={
                    ['hero-section', 'navbar-section'].includes(sectionName) &&
                    isRebrand
                      ? THEMES.MP
                      : theme
                  }
                  moduleKey={key}
                  sectionMetadata={sectionMetadata}
                  formRequestMetadata={formRequestMetadata}
                />
              );
            })}
          </TrackingProvider>
        </CalmProvider>
      </div>

      {gtm && <GTM {...gtm} />}

      <MelidataTrack
        {...calmTrack}
        isDeferred={skipCalmTrack}
        MelidataTrack={experiments}
      />

      {/* Options are inferred from frontend-config.
  See https://github.com/mercadolibre/fury_frontend-metrics/tree/master/packages/hotjar#options */}
      {hotjar && !hasRemoteModuleSections && <Hotjar id={hotjar.id} />}

      <Script priority={1}>
        {`window.__PRELOADED_STATE__ = ${serialize(
          {
            experiments,
            imagesPrefix,
            isMobile,
            isWebView,
            isRebrand,
            theme,
            translationsRebrand,
            hiddenModal,
            keepImageURLs,
            baseApiPath,
            csrfToken,
            landingConfig: {
              groupId,
              variantId,
              landingId,
              versionId,
              variantConfigurationId,
              variantName,
              head,
              productName,
              space,
              tracking: {
                gtm,
                melidata,
                hotjar,
              },
              custom: { ddrumConfig },
            },
            locale,
            device,
            scope,
            sections: filteredSections,
            trackHandle,
            mainStyles,
            landingStatus,
            isPreview,
            recaptchaSiteKey,
            coreValues: {
              userId,
              coupon,
              channel,
              subChannel,
              siteId,
              userAgent,
            },
            clientLogsEnabled,
            uuid,
            utmParams,
          },
          { isJSON: true },
        )};`}
      </Script>

      <Script priority={2} src={['vendor.js']} />
    </div>
  );
};
